import { getDeployEnv } from './deployEnv';

export interface Segment extends Record<string, () => void> {
  trackSubmit: (...args: any[]) => void;
  trackClick: (...args: any[]) => void;
  trackLink: (...args: any[]) => void;
  trackForm: (...args: any[]) => void;
  pageview: (...args: any[]) => void;
  identify: (...args: any[]) => void;
  reset: (...args: any[]) => void;
  group: (...args: any[]) => void;
  track: (...args: any[]) => void;
  ready: (...args: any[]) => void;
  alias: (...args: any[]) => void;
  debug: (...args: any[]) => void;
  page: (...args: any[]) => void;
  once: (...args: any[]) => void;
  off: (...args: any[]) => void;
  on: (...args: any[]) => void;
}
const SEGMENT_METHODS = [
  'trackSubmit',
  'trackClick',
  'trackLink',
  'trackForm',
  'pageview',
  'identify',
  'reset',
  'group',
  'track',
  'ready',
  'alias',
  'debug',
  'page',
  'once',
  'off',
  'on',
];

// Allow running in node for server-side rendering
if (typeof window !== 'undefined') {
  // This block taken from https://segment.com/docs/sources/website/analytics.js/quickstart ,
  // and modified to remove defensive code that prevents it being initialized twice

  const segment = [] as any;
  // don't clobber existing window.analytics if it exists (useful when an app loads segment)
  // in a custom way. In the future we should have apps load this object explicitly (only once)
  // in the head.
  (window as any).analytics = (window as any).analytics || segment;

  // Define a factory to create stubs. These are placeholders
  // for methods in Analytics.js so that you never have to wait
  // for it to load to actually record data. The `method` is
  // stored as the first argument, so segment can replay the data.
  segment.factory = function (method: string) {
    return function () {
      var args = Array.prototype.slice.call(arguments);
      args.unshift(method);
      segment.push(args);
      return segment;
    };
  };

  // For each segment method, generate a queueing stub.
  for (var i = 0; i < SEGMENT_METHODS.length; i++) {
    var key = SEGMENT_METHODS[i];
    segment[key] = segment.factory(key);
  }

  segment.SNIPPET_VERSION = '4.0.0';
  segment.page();
}

// The segment script overwrites window.analytics with a new object, so we can't
// directly export the `segment` object. Instead, export an object that delegates
// to `window.analytics`
const segmentWrapper = {} as Segment;
SEGMENT_METHODS.forEach((methodName) => {
  segmentWrapper[methodName] = function () {
    // @ts-ignore
    (window as any).analytics[methodName](...arguments);
  };
});

// Define a method to load Analytics.js from segment CDN
//
// The default source that tracking events are sent to is
// Web Market JS Prod (DWH: web_js_production) in production and
// Web Market JS Staging (DWH: web_js_staging) in staging.
// The request to load the script is proxied via Cloudflare Worker
// in order to circumvent ad blockers.
//
// To send tracking events to other sources, pass the source's
// write key to this function. The request to load the script
// may be blocked by ad blockers.
export const loadSegment = (key?: string) => {
  var script = document.createElement('script');
  script.type = 'text/javascript';
  script.async = true;
  if (key) {
    script.src =
      ('https:' === document.location.protocol ? 'https://' : 'http://') +
      'trkcdn.opendoor.com/analytics.js/v1/' +
      key +
      '/analytics.min.js';
  } else {
    script.src =
      ('https:' === document.location.protocol ? 'https://' : 'http://') +
      (getDeployEnv() === 'production' ? 'www.opendoor.com' : 'demo.simplersell.com') +
      '/_scripts/sgm.min.js';
  }

  // Insert our script next to the first script element.
  var first = document.getElementsByTagName('script')[0];
  // @ts-ignore
  first.parentNode.insertBefore(script, first);
};

export default segmentWrapper;
